import { useEffect, useMemo, useState } from "react";
import MaterialReactTable, {
	MRT_Updater,
	type MRT_ColumnDef,
	type MRT_ColumnFiltersState,
	type MRT_PaginationState,
	type MRT_SortingState,
} from "material-react-table";
import { ErrorHistoryElement, IntegrationPrefixes } from "../../types/kanbanizeTypes";
import { ErrHistoryProps } from "../../types/appTypes";
import { isActualError } from "../../utils/utils";
import { Row, Col, Button, Modal } from "react-bootstrap";
import { Navigate } from "react-router-dom";

const ErrorHistory = (props: ErrHistoryProps) => {
	const integration_name = process.env.REACT_APP_INTEGRATION_SERVICE;
	if (!integration_name) throw new Error("Error in environment; Could not recover integration name");

	const integration_prefix = (integration => {
		switch (integration) {
			case "GitHub":
				return "gh";
			case "GitLab":
				return "gl";
			default:
				return "gh";
		}
	})(integration_name);
	//Data of the table.
	const [data, setData] = useState<ErrorHistoryElement<typeof integration_prefix>[]>([]);
	const [isError, setIsError] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isRefetching, setIsRefetching] = useState(false);
	const [rowCount, setRowCount] = useState(0);
	//State for the table features
	const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
	const [sorting, setSorting] = useState<MRT_SortingState>([]);
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 0,
		pageSize: 5,
	});
	//State of this component
	const [ret, setRet] = useState<boolean>(false);
	const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
	const [randomTrigger, setRandomTrigger] = useState<string>("");

	const { integrationUrl, currentConfig } = props;
	const handleError = (e: string | false) => {
		props.setGeneralError(e);
	};

	//useEffect to asynchronously load data from the bot
	useEffect(() => {
		const loadTable = async () => {
			try {
				handleError(false);
				if (!currentConfig.id) throw new Error("Could not recover the repository links id.");
				if (!data.length) {
					setIsLoading(true);
				} else {
					setIsRefetching(true);
				}
				const returnedData = await loadErrHistory<typeof integration_prefix>(
					integrationUrl,
					currentConfig.id,
					pagination,
					columnFilters,
					sorting,
				);
				setData(returnedData.data);
				setRowCount(returnedData.rowNum);
				setIsError(false);
				setIsLoading(false);
				setIsRefetching(false);
			} catch (e: any) {
				setIsError(true);
				handleError(e.message);
				console.error(e);
				return;
			}
		};
		loadTable();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data.length, columnFilters, sorting, pagination, randomTrigger]);

	const columns = useMemo<MRT_ColumnDef<ErrorHistoryElement<typeof integration_prefix>>[]>(
		() => [
			{
				accessorKey: `${integration_prefix}_err_type`,
				header: "Type",
			},
			{
				accessorKey: `${integration_prefix}_err_action`,
				header: "Action",
			},
			{
				accessorKey: `${integration_prefix}_err_msg`,
				header: "Message",
			},
			{
				accessorKey: `${integration_prefix}_err_timestamp`,
				header: "Date",
				filterVariant: "range",
			},
			{
				accessorKey: `${integration_prefix}_err_repo_name`,
				header: "Repository",
			},
			{
				accessorKey: `${integration_prefix}_err_card_id`,
				header: "Card",
			},
		],
		//disable next line because its an environment variable wich should never change
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);
	const manual_operations =
		integration_name === "GitHub"
			? { manualFiltering: true, manualPagination: true, manualSorting: true }
			: { manualFiltering: false, manualPagination: false, manualSorting: false };
	const column_filter_change =
		integration_name !== "GitHub"
			? (column_fiter: MRT_Updater<MRT_ColumnFiltersState>) => {
					if (typeof column_fiter === "function") return { columnFilters: column_fiter(columnFilters) };
					setColumnFilters(column_fiter);
			  }
			: setColumnFilters;
	const pagination_change =
		integration_name !== "GitHub"
			? (pagination_change: MRT_Updater<MRT_PaginationState>) => {
					if (typeof pagination_change === "function") return { columnFilters: pagination_change(pagination) };
					setPagination(pagination_change);
			  }
			: setPagination;
	const sorting_change =
		integration_name !== "GitHub"
			? (sorting_change: MRT_Updater<MRT_SortingState>) => {
					if (typeof sorting_change === "function") return { columnFilters: sorting_change(sorting) };
					setSorting(sorting_change);
			  }
			: setSorting;
	return (
		<>
			{ret && <Navigate to="/currentLinks" replace={true} />}
			<MaterialReactTable
				columns={columns}
				data={data}
				getRowId={(row, idx) => (row.id ? row.id.toString() : idx.toString())}
				initialState={{ showColumnFilters: false }}
				onColumnFiltersChange={column_filter_change}
				onPaginationChange={pagination_change}
				onSortingChange={sorting_change}
				enableGlobalFilter={false}
				rowCount={rowCount}
				{...manual_operations}
				muiToolbarAlertBannerProps={
					isError
						? {
								color: "error",

								children: "Error loading data",
						  }
						: undefined
				}
				state={{
					columnFilters,
					isLoading,
					pagination,
					showAlertBanner: isError,
					showProgressBars: isRefetching,
					sorting,
				}}
			></MaterialReactTable>
			{integration_name !== "BitBucket" && (
				<Row>
					<p>
						Note: You can delete all the errors by clicking{" "}
						<a
							href="https://businessmap.io"
							onClick={e => {
								e.preventDefault();
								setShowConfirmModal(true);
							}}
						>
							here
						</a>
					</p>
				</Row>
			)}
			<Row>
				<Col></Col>
				<Col xs={3}>
					<Button
						className="col-12 m-1 m-3 col-3"
						variant="light"
						onClick={() => {
							setRet(true);
						}}
					>
						Return
					</Button>
				</Col>
				<Col></Col>
			</Row>

			<Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
				<Modal.Header>
					<Modal.Title>Warning</Modal.Title>
				</Modal.Header>
				<Modal.Body>Are you sure? This action will delete the error history for this repository link.</Modal.Body>
				<Modal.Footer>
					<Button
						variant="secondary"
						onClick={() => {
							try {
								setShowConfirmModal(false);
							} catch (e: any) {
								props.setGeneralError(e.message);
							}
						}}
					>
						Close
					</Button>
					<Button
						variant="danger"
						onClick={() => {
							try {
								hideErrorsInDatabase(props.integrationUrl, props.currentConfig.id);
								setShowConfirmModal(false);
								setRandomTrigger(crypto.randomUUID());
							} catch (e: any) {
								props.setGeneralError(e.message);
							}
						}}
					>
						Delete
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

const loadErrHistory = async <T extends IntegrationPrefixes>(
	integrationUrl: string,
	repoLinkId: number,
	pagination: MRT_PaginationState,
	columnFilters: MRT_ColumnFiltersState,
	sorting: MRT_SortingState,
): Promise<{ rowNum: number; data: ErrorHistoryElement<T>[] }> => {
	let url;
	if (process.env.REACT_APP_INTEGRATION_SERVICE === "GitHub") {
		url = new URL(`/api/errHistory/${repoLinkId}`, integrationUrl);
		url.searchParams.set("start", `${pagination.pageIndex * pagination.pageSize}`);
		url.searchParams.set("size", `${pagination.pageSize}`);
		url.searchParams.set("filters", JSON.stringify(columnFilters ?? []));
		// url.searchParams.set("globalFilter", globalFilter ?? "");
		url.searchParams.set("sorting", JSON.stringify(sorting ?? []));
	} else {
		url = new URL(`/api/db/errors/${repoLinkId}`, integrationUrl);
	}

	const response: Response = await fetch(url.href);
	const data: ErrorHistoryElement<T>[] = await response.json();
	if (isActualError(data)) throw data;
	const rowNum =
		process.env.REACT_APP_INTEGRATION_SERVICE === "GitHub"
			? response.headers.get("row-num")
				? parseInt(response.headers.get("row-num") || "0")
				: 0
			: data.length;
	return { rowNum, data };
};

const hideErrorsInDatabase = async (integrationUrl: string, repoLinkId?: number) => {
	if (!repoLinkId) throw new Error("Could not recover the repository links id.");
	let url;
	if (process.env.REACT_APP_INTEGRATION_SERVICE === "GitHub") {
		url = `${integrationUrl}/api/errHistory/${repoLinkId}`;
	} else {
		//GitLab and BitBucket should use same route structure
		url = `${integrationUrl}/api/db/errors/${repoLinkId}`;
	}
	const response: Response = await fetch(url, {
		method: "DELETE",
	});
	const data = await response.json();
	if (isActualError(data)) throw data;
};

export default ErrorHistory;
